import apiClient from './api'
import type { User } from '@/stores/auth'

export interface AuthResponse {
  user: User
  token: string
}

export interface OAuthUrlResponse {
  url: string
}

class AuthService {
  /**
   * Получить URL для OAuth авторизации через Google
   */
  async getGoogleAuthUrl(): Promise<string> {
    try {
      const response = await apiClient.get<OAuthUrlResponse>('/auth/login/google')
      return response.data.url
    } catch (error) {
      console.error('Error getting Google auth URL:', error)
      throw new Error('Не удалось получить ссылку для авторизации через Google')
    }
  }

  /**
   * Получить URL для OAuth авторизации через Yandex
   */
  async getYandexAuthUrl(): Promise<string> {
    try {
      const response = await apiClient.get<OAuthUrlResponse>('/auth/login/yandex')
      return response.data.url
    } catch (error) {
      console.error('Error getting Yandex auth URL:', error)
      throw new Error('Не удалось получить ссылку для авторизации через Yandex')
    }
  }

  /**
   * Инициировать OAuth авторизацию через Google
   */
  async loginWithGoogle(): Promise<void> {
    try {
      const url = await this.getGoogleAuthUrl()
      window.location.href = url
    } catch (error) {
      console.error('Error initiating Google login:', error)
      throw error
    }
  }

  /**
   * Инициировать OAuth авторизацию через Yandex
   */
  async loginWithYandex(): Promise<void> {
    try {
      const url = await this.getYandexAuthUrl()
      window.location.href = url
    } catch (error) {
      console.error('Error initiating Yandex login:', error)
      throw error
    }
  }

  /**
   * Обработать колбэк после OAuth авторизации
   * @param code - код авторизации
   * @param provider - провайдер (google/yandex)
   */
  async handleOAuthCallback(code: string, provider: 'google' | 'yandex'): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>(`/auth/callback/${provider}`, {
        code
      })
      return response.data
    } catch (error) {
      console.error('Error handling OAuth callback:', error)
      throw new Error('Ошибка при завершении авторизации')
    }
  }

  /**
   * Выход из системы
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout')
    } catch (error) {
      console.error('Error during logout:', error)
      // Не бросаем ошибку, так как даже при ошибке API нужно очистить локальную сессию
    }
  }

  /**
   * Получить данные текущего пользователя
   */
  async getCurrentUser(): Promise<User> {
    try {
      const response = await apiClient.get<User>('/auth/me')
      return response.data
    } catch (error) {
      console.error('Error getting current user:', error)
      throw new Error('Не удалось получить данные пользователя')
    }
  }

  /**
   * Проверить действительность токена
   */
  async verifyToken(): Promise<boolean> {
    try {
      await this.getCurrentUser()
      return true
    } catch (error) {
      return false
    }
  }
}

export const authService = new AuthService() 